import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/components/AuthProvider';
import { supabase } from '@/integrations/supabase/client';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    let active = true;
    const run = async () => {
      if (!user) return;
      try {
        // Check if user is admin first
        const { data: hasAdminRole } = await supabase
          .rpc('has_role', {
            _user_id: user.id,
            _role: 'admin'
          });

        if (hasAdminRole) {
          if (active) setAllowed(true);
          return;
        }

        // Check subscription status (Stripe) and native trial
        const { data, error } = await supabase.functions.invoke('check-subscription');

        // Check for session expiration in response data
        if (data?.session_expired) {
          console.warn('Session expired, signing out...');
          await supabase.auth.signOut();
          if (active) setSessionExpired(true);
          return;
        }

        if (error) {
          const errorMessage = error?.message || JSON.stringify(error);
          if (errorMessage.toLowerCase().includes('session')) {
            console.warn('Session expired (from error), signing out...');
            await supabase.auth.signOut();
            if (active) setSessionExpired(true);
            return;
          }
          throw error;
        }

        // Check if user has active Stripe subscription (active or trialing)
        const isSubscribed = data?.subscribed === true;
        const status = data?.status;
        const hasStripeAccess = isSubscribed && (status === 'active' || status === 'trialing');

        // Check native trial (7-day free trial without credit card)
        const hasNativeTrial = data?.has_native_trial === true;

        // User can access if: has Stripe subscription OR has active native trial
        const canAccess = hasStripeAccess || hasNativeTrial;

        console.log('Access check:', { hasStripeAccess, hasNativeTrial, canAccess });

        if (active) setAllowed(canAccess);
      } catch (e: any) {
        console.error('ProtectedRoute subscription check error:', e);
        const errorMessage = e?.message || String(e);
        if (errorMessage.includes('Session') && errorMessage.includes('does not exist')) {
          console.warn('Session expired in catch, signing out...');
          await supabase.auth.signOut();
          if (active) setSessionExpired(true);
          return;
        }
        if (active) setAllowed(false);
      } finally {
        if (active) setChecking(false);
      }
    };
    if (user) run();
    return () => { active = false; };
  }, [user?.id]);

  // Redirect to home if session expired
  if (sessionExpired) {
    return <Navigate to="/" replace />;
  }

  if (loading || (user && checking)) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-6">
        {/* Logo animado */}
        <img
          src="/lovable-uploads/73e4b61d-e4f1-443a-bcb7-800408ad6415.png"
          alt="OBRIM"
          className="h-16 w-auto animate-pulse"
        />
        {/* Skeleton cards */}
        <div className="flex flex-col items-center gap-3 w-full max-w-xs px-4">
          <div className="h-2 w-3/4 bg-muted rounded-full animate-pulse" />
          <div className="h-2 w-1/2 bg-muted rounded-full animate-pulse" />
        </div>
        <p className="text-sm text-muted-foreground animate-pulse">Carregando...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (allowed === false) {
    return <Navigate to="/assinatura" replace />;
  }

  return <>{children}</>;
};
